import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Paper,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  PlayArrow as RunIcon,
  ShoppingBag,
  Receipt,
  LocalOffer,
  Inventory,
  Star,
  Storage,
} from '@mui/icons-material';
import shopService from '../../../services/shop.service';
import ordersService from '../../../services/orders.service';
import promotionsService from '../../../services/promotions.service';
import inventoryService from '../../../services/inventory.service';
import reviewsService from '../../../services/reviews.service';

interface SetupModule {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  tables: string[];
  status: 'pending' | 'running' | 'success' | 'error';
  message?: string;
  setupFn: () => Promise<any>;
}

const ShopSetup: React.FC = () => {
  const [running, setRunning] = useState(false);
  const [modules, setModules] = useState<SetupModule[]>([
    {
      id: 'shop',
      name: 'Loja Principal',
      description: 'Categorias, Tipos de Loja, Planos',
      icon: <ShoppingBag color="primary" />,
      tables: ['pg_categoria_produto', 'pg_loja_tipo', 'pg_plano_loja'],
      status: 'pending',
      setupFn: shopService.setupTables.bind(shopService),
    },
    {
      id: 'orders',
      name: 'Pedidos',
      description: 'Gestao de pedidos e encomendas',
      icon: <Receipt color="info" />,
      tables: ['pg_pedido', 'pg_pedido_item', 'pg_pedido_historico'],
      status: 'pending',
      setupFn: ordersService.setupTables.bind(ordersService),
    },
    {
      id: 'promotions',
      name: 'Promocoes e Cupoes',
      description: 'Sistema de promocoes e descontos',
      icon: <LocalOffer color="secondary" />,
      tables: ['pg_promocao', 'pg_cupom', 'pg_cupom_uso'],
      status: 'pending',
      setupFn: promotionsService.setupTables.bind(promotionsService),
    },
    {
      id: 'inventory',
      name: 'Inventario',
      description: 'Controle de stock e alertas',
      icon: <Inventory color="warning" />,
      tables: ['pg_movimento_estoque', 'pg_alerta_estoque'],
      status: 'pending',
      setupFn: inventoryService.setupTables.bind(inventoryService),
    },
    {
      id: 'reviews',
      name: 'Avaliacoes',
      description: 'Avaliacoes de produtos e lojas',
      icon: <Star color="success" />,
      tables: ['pg_avaliacao_produto', 'pg_avaliacao_loja'],
      status: 'pending',
      setupFn: reviewsService.setupTables.bind(reviewsService),
    },
  ]);
  const [allComplete, setAllComplete] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => {
    const successCount = modules.filter(m => m.status === 'success').length;
    const errorCount = modules.filter(m => m.status === 'error').length;
    setAllComplete(successCount === modules.length);
    setHasErrors(errorCount > 0);
  }, [modules]);

  const updateModuleStatus = (id: string, status: SetupModule['status'], message?: string) => {
    setModules(prev => prev.map(m =>
      m.id === id ? { ...m, status, message } : m
    ));
  };

  const runSetupForModule = async (module: SetupModule) => {
    updateModuleStatus(module.id, 'running');
    try {
      const response = await module.setupFn();
      if (response.success) {
        updateModuleStatus(module.id, 'success', response.message || 'Configurado com sucesso');
      } else {
        updateModuleStatus(module.id, 'error', response.error || 'Erro desconhecido');
      }
    } catch (error: any) {
      console.error(`Error setting up ${module.name}:`, error);
      updateModuleStatus(module.id, 'error', error.message || 'Erro ao configurar');
    }
  };

  const runAllSetups = async () => {
    setRunning(true);

    // Reset all to pending
    setModules(prev => prev.map(m => ({ ...m, status: 'pending', message: undefined })));

    // Run each setup sequentially
    for (const module of modules) {
      await runSetupForModule(module);
    }

    setRunning(false);
  };

  const getStatusIcon = (status: SetupModule['status']) => {
    switch (status) {
      case 'running':
        return <CircularProgress size={20} />;
      case 'success':
        return <CheckIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <Storage color="disabled" />;
    }
  };

  const getStatusChip = (status: SetupModule['status']) => {
    switch (status) {
      case 'running':
        return <Chip size="small" label="A configurar..." color="info" />;
      case 'success':
        return <Chip size="small" label="Configurado" color="success" />;
      case 'error':
        return <Chip size="small" label="Erro" color="error" />;
      default:
        return <Chip size="small" label="Pendente" color="default" />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Configuracao do PagaliShop
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure todas as tabelas necessarias para o funcionamento do PagaliShop
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={running ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
          onClick={runAllSetups}
          disabled={running}
        >
          {running ? 'A configurar...' : 'Executar Setup Completo'}
        </Button>
      </Box>

      {allComplete && !hasErrors && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CheckIcon />}>
          Todas as tabelas do PagaliShop foram configuradas com sucesso!
        </Alert>
      )}

      {hasErrors && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Alguns modulos apresentaram erros. Verifique os detalhes abaixo.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <List>
            {modules.map((module, index) => (
              <React.Fragment key={module.id}>
                {index > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {getStatusChip(module.status)}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={module.status === 'running' ? <CircularProgress size={16} /> : <SettingsIcon />}
                        onClick={() => runSetupForModule(module)}
                        disabled={running || module.status === 'running'}
                      >
                        Configurar
                      </Button>
                    </Box>
                  }
                  sx={{ py: 2 }}
                >
                  <ListItemIcon>
                    {module.status === 'pending' ? module.icon : getStatusIcon(module.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography fontWeight={600}>{module.name}</Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {module.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {module.tables.map(table => (
                            <Chip
                              key={table}
                              label={table}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ))}
                        </Box>
                        {module.message && (
                          <Typography
                            variant="caption"
                            color={module.status === 'error' ? 'error' : 'success.main'}
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {module.message}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      <Paper sx={{ mt: 3, p: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" gutterBottom>
          Informacoes do Setup
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Este assistente cria automaticamente todas as tabelas necessarias para o funcionamento do PagaliShop.
          O processo e seguro e pode ser executado varias vezes - tabelas existentes nao serao afectadas.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600}>Tabelas que serao criadas:</Typography>
          <Typography variant="body2" color="text.secondary">
            - <strong>Loja:</strong> Categorias de produtos, tipos de loja, planos de subscricao
          </Typography>
          <Typography variant="body2" color="text.secondary">
            - <strong>Pedidos:</strong> Pedidos, itens de pedido, historico de alteracoes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            - <strong>Promocoes:</strong> Promocoes, cupoes de desconto, registo de utilizacao
          </Typography>
          <Typography variant="body2" color="text.secondary">
            - <strong>Inventario:</strong> Movimentos de stock, alertas de stock baixo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            - <strong>Avaliacoes:</strong> Avaliacoes de produtos e lojas
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ShopSetup;
